import React, { useContext, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { UserContext } from '../contexts/UserContext';
import mediaService from '../services/mediaService';
import mediaCategoriesService, { MediaCategory } from '../services/mediaCategoriesService';
import {
  Upload,
  Trash2,
  Download,
  Edit2,
  Plus,
  X,
  AlertCircle,
  CheckCircle,
  Clock,
  Eye,
  FolderOpen,
} from 'lucide-react';

interface MediaItem {
  path: string;
  name: string;
  size?: number;
  uploadedAt?: string;
  url?: string;
  mediaCategoryId?: string;
}

interface FormState {
  filePath: string;
  file: File | null;
  mediaCategoryId?: string;
}

export default function MediaStoragePage() {
  const { userProfile } = useContext(UserContext);
  const organizationId = userProfile?.orgId;
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [mediaCategories, setMediaCategories] = useState<MediaCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>({
    filePath: '',
    file: null,
    mediaCategoryId: undefined,
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);

  useEffect(() => {
    if (!organizationId) return;

    const unsubscribe = mediaService.subscribeMediaChanges((evt: any) => {
      if (evt.organizationId !== organizationId) return;

      if (evt.action === 'added' && evt.item) {
        const newItem = evt.item as MediaItem;
        setMediaItems((prev) => {
          const key = newItem.path || newItem.url || newItem.name;
          if (prev.some((p) => (p.path || p.url || p.name) === key)) return prev;
          return [newItem, ...prev];
        });
      }

      if (evt.action === 'deleted' && evt.path) {
        setMediaItems((prev) => prev.filter((p) => p.path !== evt.path));
      }
    });

    return () => unsubscribe();
  }, [organizationId]);

  const categoryLookup = useMemo(() => {
    const map = new Map<string, string>();
    mediaCategories.forEach((category) => {
      map.set(category.mediaCategoryId, category.name);
    });
    return map;
  }, [mediaCategories]);

  const getCategoryName = (categoryId?: string) => {
    if (!categoryId) return 'Uncategorized';
    return categoryLookup.get(categoryId) || 'Uncategorized';
  };

  useEffect(() => {
    loadMediaItems();
    loadMediaCategories();
  }, [organizationId]);

  const loadMediaItems = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      const files = await mediaService.getOrganizationFiles(organizationId);
      setMediaItems(files);
    } catch (error) {
      console.error('Failed to load media items:', error);
      setError('Failed to load media files');
    } finally {
      setLoading(false);
    }
  };

  const loadMediaCategories = async () => {
    if (!organizationId) return;
    
    try {
      const categories = await mediaCategoriesService.getCategories(organizationId);
      setMediaCategories(categories);
    } catch (error) {
      console.error('Failed to load media categories:', error);
      // Don't show error - categories are optional
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validation = mediaService.validateFile(file);
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
        return;
      }
      
      // Set file and auto-populate filePath with filename
      const fileName = file.name;
      setFormState({ 
        ...formState, 
        file,
        filePath: fileName, // Auto-populate with just the filename
      });
      setError(null);
    }
  };

  const handleFilePathChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({ ...formState, filePath: e.target.value });
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormState({ 
      ...formState, 
      mediaCategoryId: e.target.value || undefined 
    });
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formState.file || !formState.filePath || !organizationId) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editingPath) {
        // Update existing file
        await mediaService.updateFile(organizationId, formState.filePath, formState.file, formState.mediaCategoryId);
        setSuccess('File updated successfully');
      } else {
        // Upload new file
        await mediaService.uploadFile(organizationId, formState.filePath, formState.file, formState.mediaCategoryId);
        setSuccess('File uploaded successfully');
      }

      // Reload list to ensure fresh data (and pick up any server-side transforms/URLs)
      await loadMediaItems();

      setEditingPath(null);

      // Reset form
      setFormState({ filePath: '', file: null, mediaCategoryId: undefined });
      setShowUploadForm(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to upload file'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (path: string) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await mediaService.deleteFile(organizationId!, path);
      setMediaItems(mediaItems.filter((item) => item.path !== path));
      setSuccess('File deleted successfully');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete file'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item: MediaItem) => {
    setEditingPath(item.path);
    setFormState({
      filePath: item.path,
      file: null,
      mediaCategoryId: item.mediaCategoryId,
    });
    setShowUploadForm(true);
  };

  const handleDownload = (item: MediaItem) => {
    if (!item.url) {
      setError('File URL not available');
      return;
    }
    const fullUrl = mediaService.toAbsoluteUrl(item.url);
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = item.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePreview = (item: MediaItem) => {
    if (!item.url) {
      setError('File URL not available');
      return;
    }
    const fullUrl = mediaService.toAbsoluteUrl(item.url);
    setPreviewUrl(fullUrl);
    setImageLoadError(false);
    setIsPreviewModalOpen(true);
  };

  const handleCancel = () => {
    setShowUploadForm(false);
    setEditingPath(null);
    setFormState({ filePath: '', file: null, mediaCategoryId: undefined });
    setError(null);
  };

  const filteredItems = mediaItems.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <Upload className="w-8 h-8 text-indigo-600" />
            Media Storage
          </h1>
          <p className="text-gray-600 mt-1">Manage your organization's media files</p>
        </div>
        <button
          onClick={() => setShowUploadForm(true)}
          disabled={showUploadForm}
          className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New File
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-600 hover:text-red-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-green-800">{success}</p>
          <button
            onClick={() => setSuccess(null)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {editingPath ? 'Update File' : 'Upload New File'}
          </h2>
          
          <form onSubmit={handleUpload} className="space-y-4">
            {/* Select File - Now First */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                onChange={handleFileSelect}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required={!editingPath}
              />
              {formState.file && (
                <p className="text-xs text-gray-600 mt-2">
                  Selected: {formState.file.name} ({mediaService.formatFileSize(formState.file.size)})
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">Max file size: 50MB</p>
            </div>

            {/* File Path - Now Second, auto-populated */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File Path <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formState.filePath}
                onChange={handleFilePathChange}
                placeholder="filename.ext or folder/filename.ext"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Auto-populated from filename. Add folder path if needed (e.g., documents/file.pdf)
              </p>
            </div>

            {/* Category Selector - Optional */}
            {mediaCategories.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <FolderOpen className="w-4 h-4 text-gray-500" />
                  Category (Optional)
                </label>
                <select
                  value={formState.mediaCategoryId || ''}
                  onChange={handleCategoryChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- No Category --</option>
                  {mediaCategories.map((category) => (
                    <option key={category.mediaCategoryId} value={category.mediaCategoryId}>
                      {category.name}
                      {category.description && ` - ${category.description}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Organize files by category for easier management
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={loading || !formState.file || !formState.filePath}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Upload className="w-4 h-4" />
                {loading ? 'Uploading...' : editingPath ? 'Update' : 'Upload'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="flex items-center gap-2 px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search Bar */}
      <div>
        <input
          type="text"
          placeholder="Search files by name or path..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Files Table */}
      <div className="bg-white/60 backdrop-blur-md rounded-xl shadow-sm border border-indigo-100/50 overflow-hidden">
        {filteredItems.length === 0 ? (
          <div className="p-12 text-center">
            <Upload className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg">
              {mediaItems.length === 0
                ? 'No files uploaded yet. Click "New File" to get started.'
                : 'No files match your search.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    File Name
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Path
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                    Uploaded
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr
                    key={item.path}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 text-sm text-gray-800 font-medium">
                      {item.name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 break-all">
                      {item.path}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {getCategoryName(item.mediaCategoryId)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.size
                        ? mediaService.formatFileSize(item.size)
                        : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {item.uploadedAt ? (
                        <span className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {new Date(item.uploadedAt).toLocaleDateString()}
                        </span>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handlePreview(item)}
                          disabled={loading}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Preview"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownload(item)}
                          disabled={loading}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Download"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          disabled={loading}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Edit"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.path)}
                          disabled={loading}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {mediaItems.length > 0 && (
        <div className="text-right text-sm text-gray-600">
          Showing {filteredItems.length} of {mediaItems.length} files
        </div>
      )}

      {/* Preview Modal */}
      {isPreviewModalOpen && previewUrl && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99999] flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl relative max-w-2xl max-h-[90vh] flex flex-col">
            <button
              onClick={() => {
                setIsPreviewModalOpen(false);
                setPreviewUrl(null);
              }}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100/80 rounded-full transition-colors z-10"
              title="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="p-4 overflow-auto flex-1 flex items-start justify-center">
              {imageLoadError ? (
                <div className="text-center text-gray-500 p-8">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>Unable to preview this file</p>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="max-w-full object-contain rounded-lg"
                  onError={() => setImageLoadError(true)}
                />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
